<script setup>
import { computed, defineModel, ref, watch } from 'vue'
import {
	Combobox,
	ComboboxInput,
	ComboboxOptions,
	ComboboxOption, ComboboxLabel, ComboboxButton
} from '@headlessui/vue'
import { api, ApiGatewayResponse } from '@/api/ApiGateway.js'
import { CheckIcon } from '@heroicons/vue/20/solid'
import { refDebounced } from '@vueuse/core'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/20/solid'
import { Float } from '@headlessui-float/vue'
import ValidationError from '@/components/Forms/InputError.vue'

const props = defineProps({
	id: { type: String, required: true },
	items: { type: Array, default: () => [] },
	title: { type: String, default: '' },
	placeholder: { type: String, default: '' },
	showOptionsOnTop: { type: Boolean, default: false },
	inputHeight: { type: String, default: '' },
	labelTextSize: { type: String, default: '' },
	labelClass: { type: String, default: '' },//AJL
	errors: { type: ApiGatewayResponse, default: null },
	errorField: { type: String, default: null },
	readonly: { type: Boolean, default: false },
	disabled: { type: Boolean, default: false },
	icon: { type: String, default: 'none' },
	maxLength: { type: Number, default: 255 },
	endpoint: { type: String, default: null },
	endpoint_params: { type: Object, default: {} },
	maxSearchResultsHeight: { type: String, default: 'max-h-40' },
	limit: { type: Number, default: 10 }, //Limit number of results
	showOptionsOutsideModal: { type: Boolean, default: false },
	required: { type: Boolean, default: false },//true border red when value blank
})

const selectedPeople = defineModel({
	type: Array,
	default: () => []
})

const query = ref('')
const searchResults = ref(sortPeople(selectedPeople))
const comboboxRef = ref(null)
const isLoading = ref(false)

const labelCustomClass = computed(() =>
{
	return [props.labelClass, props.labelTextSize].join(' ')
})

const selectedUsers = computed(() => {
	return selectedPeople.value.map(person => {
		return person.full_name + ' (' + person.email + ')'
	}).join(', ')
})

/**
 *
 * @param p_person
 * @returns {boolean}
 */
function isSelected(p_person)
{
	return selectedPeople.value.findIndex((p_item) => p_item.id === p_person.id) >= 0;
}

const debouncedQuery = refDebounced(query, 150)
watch(debouncedQuery, (p_value) => {

	if (!p_value) {
		//selectedItem.value = null
		isLoading.value = false
		searchResults.value = [...sortedPeople.value];
		return
	}

	const params = props.endpoint_params
	params['search'] = p_value;
	params['limit'] = props.limit


	isLoading.value = true

	api.get(props.endpoint, props.endpoint_params).then((response) => {
		if (response.hasError() || !response.body()) {
			isLoading.value = false
			return
		}

		let records = response.body().data;

		records = records.filter((p_item) => {
			return !isSelected(p_item)
		})

		records = [...sortedPeople.value, ...records];

		//Add a "data" property to your returned value to include extra data beyond id and name (see Edit Org Groups)
		searchResults.value = records.map((record) => {
			return record
		})

		isLoading.value = false
	})
})

const sortedPeople = computed(() => {
	return sortPeople(selectedPeople);
	/*return [...selectedPeople.value].sort((a, b) => {
		const name_a = a.given_name + ' ' + a.family_name;
		const name_b = b.given_name + ' ' + b.family_name;
		return name_a.localeCompare(name_b);
	});*/
});

function sortPeople(p_data)
{
	return [...p_data.value].sort((a, b) => {
		return a.full_name.localeCompare(b.full_name);
	});
}

const placeholderValues = computed(() => {
	const selected = sortedPeople.value.map(p_item => p_item.full_name)
	return selected.length === 0 ? props.placeholder : selected.join(", ")
});

function inputChanged(p_value)
{
	query.value = p_value
}

function hasError()
{
	return props.errors?.validationErrors()?.find((error) => error.source === props.errorField) != null
}

const comboBoxCustomClass = computed(() =>
{
	const classes = ['combobox-custom-classes '];

	classes.push('border ');
	if (!props.disabled)
	{
		classes.push('focus:ring-primary-bold-hover focus:border-primary-bold-hover group-hover:ring-1 group-hover:ring-primary-bold-hover group-hover:border-primary-bold-hover');
	}
	classes.push(hasError() ? 'border-error' : 'border-neutral');


	return classes.join(' ')
})

</script>

<template>
	<div class="mb-4">
		<Combobox as="div" v-model="selectedPeople" multiple v-slot="{ open }">
			<div ref="comboboxRef">
				<ComboboxLabel v-if="props.title" class="leading-6 mb-3 block text-label font-normal" :class="labelCustomClass">{{ props.title }}</ComboboxLabel>
			</div>
			<div class="relative">
				<Float
					:portal="showOptionsOutsideModal"
					adaptiveWidth
					:placement="showOptionsOnTop ? 'top-start' : 'bottom-start'"
				>
					<div class="input-group group">
						<!-- Blur resets the query so next time a user types a request is executed (needed to work with the include defaults cbx when running a report) -->
						<ComboboxInput :placeholder="open ? props.placeholder : props.disabled ? '' : placeholderValues"
													 @change="inputChanged($event.target.value)"
													 @blur="inputChanged('')"
													 :title="selectedUsers"
													 :disabled="props.disabled"
													 class="userpilot-mask combobox-input block w-full font-medium rounded bg-neutral py-3 pl-4 pr-12 text-label outline-none
						               placeholder:text-placeholder focus:ring-1 "
													 :class="comboBoxCustomClass"
						/>
						<div class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
							<svg v-if="isLoading" class="animate-spin h-5 w-5 text-primary fill-icon-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
								<title>Searching</title>
								<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
								<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
							</svg>
							<!--					<MagnifyingGlassIcon v-else class="size-5 group-hover:text-primary-hover" aria-label="Magnifying Glass" />-->
							<ComboboxButton>
								<div v-if="!props.disabled">
									<ChevronUpIcon v-if="open" class="h-5 w-5 text-icon-neutral mt-1 group-hover/outer:fill-icon-primary-dark " aria-hidden="true" />
									<ChevronDownIcon v-else class="h-5 w-5 text-icon-neutral mt-1 group-hover/outer:fill-icon-primary-dark " aria-hidden="true" />
								</div>
							</ComboboxButton>
						</div>
					</div>

					<ComboboxOptions class="combobox-options absolute z-10 mt-1 w-full overflow-auto rounded bg-neutral mb-1 text-base font-medium ring-1 ring-primary-bold-hover border border-primary-bold-hover focus:outline-none sm:text-sm"
					                 :class="[{ 'top-full': !showOptionsOnTop, 'bottom-full': showOptionsOnTop }, maxSearchResultsHeight]" >
						<div v-if="!isLoading">
							<ComboboxOption  v-for="person in searchResults" :key="person.id" :value="person">
								<li :class="[
							'relative cursor-default select-none py-2 pl-3 pr-4 text-placeholder',
							isSelected(person) ? 'bg-primary-subtle outline-none' : 'text-label',]">
									<div class="flex flex-row justify-between">
										<div :class="['userpilot-mask block truncate whitespace-pre-wrap', isSelected(person) ? 'text-label' : '']">
											{{ person.full_name + ' (' + person.email + ')' }}
										</div>
										<div class="w-6">
											<CheckIcon v-show="isSelected(person)" class="h-4 w-4 mt-1 ml-1 text-icon-neutral" aria-hidden="true" />
										</div>
									</div>
								</li>
							</ComboboxOption>
						</div>


						<ComboboxOption v-if="searchResults.length === 0" :disabled="true" >
							<li :class="['relative cursor-default select-none py-2 pl-3 pr-4 text-placeholder' ]">
								<div class="flex flex-row">
									<div v-if="isLoading">Searching...</div>
									<div v-else-if="query == ''">Enter text to search...</div>
									<div v-else>No results found</div>
								</div>
							</li>
						</ComboboxOption>

					</ComboboxOptions>
				</Float>
			</div>

		</Combobox>
		<ValidationError :errors="props.errors" :field="props.errorField" class="mt-2 ml-3.5" />
	</div>
</template>

<style scoped>

</style>